import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  BadgeCheck, Building2, FlaskConical, Globe2, Maximize2, Minus, Plus,
  Save, ShoppingBag, Warehouse, X,
} from 'lucide-react';
import {
  ComposableMap, Geographies, Geography, ZoomableGroup,
} from 'react-simple-maps';

import { useXomicsAuth } from './AuthContext';
import AddressPicker from './AddressPicker';
import {
  createLocation, loadLocationPlace, loadManagedLocations,
  searchLocationPlaces, setLocationWebsiteDefault, updateLocation, updateLocationCountries,
} from './api';
import { tx } from './i18n';
import { codeLabel } from './labels';
import {
  Empty, Failed, Head, Loading, Pill, SearchField, XomicsSelect,
} from './parts';
import {
  COUNTRY_REGION_PRESETS, countryCodeForGeography, countryName, ISO_COUNTRIES,
} from './countries';
import { GEO_URL } from './routeMap';
import { errorMessage, Field, fieldAria } from './ui';
import { useLiveResource } from './useLive';
import './storeOffice.css';

const EMPTY = Object.freeze({
  id: null,
  code: '',
  kind: 'office',
  name: '',
  legalName: '',
  phone: '',
  email: '',
  isActive: true,
  showOnWebsite: false,
  websiteOrder: '',
  // The office whose store a country gets that no office is responsible for.
  isWebsiteDefault: false,
  placeId: '',
  addressChanged: false,
  addressLabel: '',
  address: Object.freeze({
    line1: '', line2: '', postalCode: '', city: '', region: '', countryCode: '',
  }),
  coordinates: null,
  responsibleCountries: [],
});

const MAP_HOME = Object.freeze({ coordinates: [0, 0], zoom: 1 });
const MAP_MAX_ZOOM = 6;

function formOf(location) {
  if (!location) return { ...EMPTY };
  return {
    id: location.id,
    code: location.code,
    kind: location.kind,
    name: location.name,
    legalName: location.legalName || '',
    phone: location.phone || '',
    email: location.email || '',
    isActive: location.isActive,
    showOnWebsite: location.showOnWebsite === true,
    websiteOrder: String(location.websiteOrder ?? 0),
    isWebsiteDefault: location.isWebsiteDefault === true,
    placeId: location.placeId || '',
    addressChanged: false,
    addressLabel: location.formattedAddress || [
      location.address?.line1,
      location.address?.postalCode,
      location.address?.city,
      location.address?.countryCode,
    ].filter(Boolean).join(', '),
    address: {
      line1: location.address?.line1 || '',
      line2: location.address?.line2 || '',
      postalCode: location.address?.postalCode || '',
      city: location.address?.city || '',
      region: location.address?.region || '',
      countryCode: location.address?.countryCode || '',
    },
    coordinates: location.coordinates ? { ...location.coordinates } : null,
    responsibleCountries: [...(location.responsibleCountries || [])],
  };
}

function LocationNetwork({ locations, responsibilities }) {
  const active = locations.filter((location) => location.isActive).length;
  const offices = locations.filter((location) => location.kind === 'office').length;
  const laboratories = locations.filter((location) => location.kind === 'laboratory').length;
  const warehouses = locations.filter((location) => location.kind === 'warehouse').length;
  const shops = locations.filter((location) => location.kind === 'retail').length;
  return (
    <section className="xo-location-network" aria-label={tx('ui.locations.network_title')}>
      <div className="xo-location-network-lead">
        <span><Globe2 aria-hidden="true" />{tx('ui.locations.network_eyebrow')}</span>
        <strong>{responsibilities.length.toLocaleString()}</strong>
        <p>{tx('ui.locations.network_country_count')}</p>
      </div>
      <div className="xo-location-network-route" aria-hidden="true"><span /><i /><span /><i /><span /></div>
      <dl>
        <div><dt>{tx('ui.locations.offices')}</dt><dd>{offices.toLocaleString()}</dd></div>
        <div><dt>{tx('ui.locations.laboratories')}</dt><dd>{laboratories.toLocaleString()}</dd></div>
        {warehouses > 0 && <div><dt>{tx('ui.locations.warehouses')}</dt><dd>{warehouses.toLocaleString()}</dd></div>}
        {shops > 0 && <div><dt>{tx('ui.locations.retail_shops')}</dt><dd>{shops.toLocaleString()}</dd></div>}
        <div><dt>{tx('ui.locations.active_network')}</dt><dd>{active.toLocaleString()} / {locations.length.toLocaleString()}</dd></div>
      </dl>
    </section>
  );
}

function CountryResponsibilityEditor({
  value, onChange, responsibilities, locations, currentLocationId, disabled,
}) {
  const [query, setQuery] = useState('');
  const [hovered, setHovered] = useState(null);
  const [mapPosition, setMapPosition] = useState(MAP_HOME);
  const selected = useMemo(() => new Set(value), [value]);
  const locationsById = useMemo(
    () => new Map(locations.map((location) => [Number(location.id), location])),
    [locations],
  );
  const ownerByCountry = useMemo(
    () => new Map(responsibilities.map((entry) => [entry.countryCode, Number(entry.locationId)])),
    [responsibilities],
  );
  const suggestions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return [];
    return ISO_COUNTRIES.filter((country) => !selected.has(country.code)
      && (country.code.toLowerCase().includes(needle) || country.name.toLowerCase().includes(needle)))
      .slice(0, 7);
  }, [query, selected]);

  function owner(code) {
    return locationsById.get(ownerByCountry.get(code)) || null;
  }

  function replace(next) {
    onChange([...new Set(next)].sort());
  }

  function toggle(code) {
    if (disabled || !code) return;
    replace(selected.has(code) ? value.filter((entry) => entry !== code) : [...value, code]);
  }

  function add(code) {
    replace([...value, code]);
    setQuery('');
  }

  function toggleRegion(countries) {
    if (disabled) return;
    const region = new Set(countries);
    const allSelected = countries.every((code) => selected.has(code));
    replace(allSelected
      ? value.filter((code) => !region.has(code))
      : [...value, ...countries]);
  }

  function changeZoom(delta) {
    setMapPosition((current) => ({
      ...current,
      zoom: Math.max(1, Math.min(MAP_MAX_ZOOM, current.zoom + delta)),
    }));
  }

  function resetMap() {
    setMapPosition({ coordinates: [...MAP_HOME.coordinates], zoom: MAP_HOME.zoom });
  }

  const hoveredOwner = hovered ? owner(hovered) : null;
  return (
    <section className="xo-country-responsibility-editor">
      <header>
        <div>
          <p className="xo-eyebrow">{tx('ui.locations.country_responsibilities')}</p>
          <h3>{tx('ui.locations.country_map_title')}</h3>
          <p>{tx('ui.locations.country_hint')}</p>
        </div>
        <span>{tx('ui.locations.selected_count', { count: value.length })}</span>
      </header>
      <div className="xo-country-responsibility-grid">
        <div className="xo-country-map">
          <div className="xo-country-map-canvas">
            <ComposableMap projection="geoEqualEarth" projectionConfig={{ scale: 147 }}
              width={760} height={390} role="group" aria-label={tx('ui.locations.country_map_label')}>
              <ZoomableGroup center={mapPosition.coordinates} zoom={mapPosition.zoom}
                minZoom={1} maxZoom={MAP_MAX_ZOOM} onMoveEnd={setMapPosition}>
                <Geographies geography={GEO_URL}>
                  {({ geographies }) => geographies.map((geography) => {
                    const code = countryCodeForGeography(geography);
                    const isSelected = code !== null && selected.has(code);
                    const ownedElsewhere = code !== null && ownerByCountry.has(code)
                      && ownerByCountry.get(code) !== Number(currentLocationId);
                    return (
                      <Geography key={geography.rsmKey} geography={geography}
                        className={`xo-country-geography${isSelected ? ' is-selected' : ''}${ownedElsewhere ? ' is-owned' : ''}`}
                        vectorEffect="non-scaling-stroke"
                        tabIndex={code && !disabled ? 0 : -1}
                        role={code ? 'button' : undefined}
                        aria-label={code ? `${countryName(code)} · ${code}` : geography.properties.name}
                        aria-pressed={code ? isSelected : undefined}
                        onMouseEnter={() => setHovered(code)} onMouseLeave={() => setHovered(null)}
                        onFocus={() => setHovered(code)} onBlur={() => setHovered(null)}
                        onClick={() => toggle(code)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggle(code);
                          }
                        }} />
                    );
                  })}
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
          </div>
          <div className="xo-country-map-controls" aria-label={tx('ui.locations.map_controls')}>
            <button type="button" onClick={() => changeZoom(1)}
              disabled={mapPosition.zoom >= MAP_MAX_ZOOM}
              aria-label={tx('ui.locations.zoom_in')}><Plus aria-hidden="true" /></button>
            <button type="button" onClick={() => changeZoom(-1)}
              disabled={mapPosition.zoom <= 1}
              aria-label={tx('ui.locations.zoom_out')}><Minus aria-hidden="true" /></button>
            <button type="button" onClick={resetMap}
              aria-label={tx('ui.locations.reset_map')}><Maximize2 aria-hidden="true" /></button>
          </div>
          <p className="xo-country-map-navigation">{tx('ui.locations.map_navigation')}</p>
          <div className="xo-country-map-legend">
            <span className="is-selected">{tx('ui.locations.legend_selected')}</span>
            <span className="is-owned">{tx('ui.locations.legend_other_office')}</span>
            <span>{tx('ui.locations.legend_unassigned')}</span>
          </div>
          {hovered && (
            <div className="xo-country-map-hover">
              <strong>{countryName(hovered)}</strong><span>{hovered}</span>
              {hoveredOwner && <small>{tx('ui.locations.current_owner', { code: hoveredOwner.code })}</small>}
            </div>
          )}
        </div>
        <aside className="xo-country-selection">
          <div className="xo-country-quick-select">
            <div>
              <strong>{tx('ui.locations.quick_select')}</strong>
              <button type="button" className="xo-link" disabled={disabled || value.length === 0}
                onClick={() => replace([])}>{tx('ui.locations.clear_selection')}</button>
            </div>
            <div className="xo-country-region-actions">
              {COUNTRY_REGION_PRESETS.map((preset) => {
                const complete = preset.countries.every((code) => selected.has(code));
                return (
                  <button type="button" key={preset.code} disabled={disabled}
                    className={complete ? 'is-selected' : ''} aria-pressed={complete}
                    onClick={() => toggleRegion(preset.countries)}>
                    <span>{tx(preset.label)}</span><small>{preset.countries.length}</small>
                  </button>
                );
              })}
            </div>
            <p>{tx('ui.locations.quick_select_hint')}</p>
          </div>
          <div className="xo-country-search">
            <SearchField value={query} disabled={disabled} onChange={setQuery}
              placeholder={tx('ui.locations.search_countries')}
              label={tx('ui.locations.search_countries')} />
            {suggestions.length > 0 && (
              <div className="xo-country-suggestions xo-search-results">
                {suggestions.map((country) => {
                  const currentOwner = owner(country.code);
                  return (
                    <button type="button" key={country.code} onClick={() => add(country.code)}>
                      <strong>{country.code}</strong><span>{country.name}</span>
                      {currentOwner && <small>{currentOwner.code}</small>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="xo-country-selection-head">
            <strong>{tx('ui.locations.selected_countries')}</strong>
            <span>{value.length.toLocaleString()}</span>
          </div>
          {value.length === 0 ? (
            <p className="xo-country-selection-empty">{tx('ui.locations.no_countries_selected')}</p>
          ) : (
            <ul>
              {value.map((code) => {
                const currentOwner = owner(code);
                const moves = currentOwner && currentOwner.id !== currentLocationId;
                return (
                  <li key={code}>
                    <span><strong>{code}</strong><small>{countryName(code)}</small></span>
                    {moves && <em>{tx('ui.locations.reassigns_from', { code: currentOwner.code })}</em>}
                    {!disabled && (
                      <button type="button" aria-label={tx('ui.locations.remove_country', { code })}
                        onClick={() => toggle(code)}><X aria-hidden="true" /></button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>
      <footer><Globe2 aria-hidden="true" /><div>
        <p>{tx('ui.locations.country_effect')}</p>
        <p>{tx('ui.locations.country_exclusions')}</p>
      </div></footer>
    </section>
  );
}

function addressCoordinate(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(6) : '';
}

function StructuredAddress({ address, coordinates, disabled, onLine2Change }) {
  if (!address?.line1) return null;
  return (
    <section className="xo-location-address-fields" aria-label={tx('ui.locations.verified_fields')}>
      <header><span><BadgeCheck aria-hidden="true" />{tx('ui.locations.google_verified')}</span>
        <p>{tx('ui.locations.separate_columns')}</p></header>
      <div className="xo-location-address-grid">
        <div className="is-wide"><Field id="location-address-line1" label={tx('ui.locations.address_line1')}>
          <input {...fieldAria('location-address-line1')} type="text" value={address.line1} readOnly />
        </Field></div>
        <div className="is-wide"><Field id="location-address-line2" label={tx('ui.locations.address_line2')}>
          <input {...fieldAria('location-address-line2')} type="text" value={address.line2}
            placeholder={tx('ui.locations.address_line2_placeholder')}
            disabled={disabled} onChange={(event) => onLine2Change(event.target.value)} />
        </Field></div>
        <Field id="location-postal-code" label={tx('ui.locations.postal_code')}>
          <input {...fieldAria('location-postal-code')} type="text" value={address.postalCode} readOnly />
        </Field>
        <Field id="location-city" label={tx('ui.locations.city')}>
          <input {...fieldAria('location-city')} type="text" value={address.city} readOnly />
        </Field>
        <Field id="location-region" label={tx('ui.locations.region')}>
          <input {...fieldAria('location-region')} type="text" value={address.region} readOnly />
        </Field>
        <Field id="location-country-code" label={tx('ui.locations.country_code')}>
          <input {...fieldAria('location-country-code')} type="text" value={address.countryCode} readOnly />
        </Field>
        <Field id="location-latitude" label={tx('ui.locations.latitude')}>
          <input {...fieldAria('location-latitude')} type="text"
            value={addressCoordinate(coordinates?.latitude)} readOnly />
        </Field>
        <Field id="location-longitude" label={tx('ui.locations.longitude')}>
          <input {...fieldAria('location-longitude')} type="text"
            value={addressCoordinate(coordinates?.longitude)} readOnly />
        </Field>
      </div>
    </section>
  );
}

export default function XomicsLocations() {
  const { permissions } = useXomicsAuth();
  const mayManage = permissions.includes('LOCATION_MANAGE');
  const load = useCallback(() => loadManagedLocations(), []);
  const resource = useLiveResource(['locations'], load);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const locations = useMemo(() => resource.data?.locations || [], [resource.data?.locations]);
  const selected = useMemo(
    () => locations.find((location) => location.id === selectedId) || null,
    [locations, selectedId],
  );

  useEffect(() => {
    if (creating) return;
    if (locations.length === 0) {
      setSelectedId(null);
      setForm({ ...EMPTY });
      return;
    }
    const next = selected || locations[0];
    if (selectedId !== next.id) setSelectedId(next.id);
    setForm(formOf(next));
  }, [creating, locations, selected, selectedId]);

  function set(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function choose(location) {
    setCreating(false);
    setSelectedId(location.id);
    setForm(formOf(location));
    setError('');
    setNotice('');
  }

  function startCreate() {
    setCreating(true);
    setSelectedId(null);
    setForm({ ...EMPTY });
    setError('');
    setNotice('');
  }

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    if (creating && !form.placeId) {
      setError(tx('ui.locations.verified_address_required'));
      setSaving(false);
      return;
    }
    try {
      const details = {
        code: form.code,
        kind: form.kind,
        name: form.name,
        legalName: form.legalName || null,
        phone: form.phone || null,
        email: form.email || null,
        addressLine2: form.address?.line2 || null,
        isActive: form.isActive,
        showOnWebsite: form.kind === 'office' && form.showOnWebsite,
        websiteOrder: form.websiteOrder === '' ? undefined : Number(form.websiteOrder),
        ...(creating || form.addressChanged ? { placeId: form.placeId } : {}),
      };
      const result = creating
        ? await createLocation(details)
        : await updateLocation(form.id, details);
      const id = creating ? result.id : form.id;
      if (form.kind === 'office') {
        await updateLocationCountries(id, form.responsibleCountries);
      }
      // The mark moves to this office; the one that had it loses it.
      if (form.kind === 'office' && form.isWebsiteDefault && (creating || selected?.isWebsiteDefault !== true)) {
        await setLocationWebsiteDefault(id);
      }
      setCreating(false);
      setSelectedId(id);
      await resource.refresh();
      setNotice(tx('ui.locations.saved'));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSaving(false);
    }
  }

  if (resource.loading && resource.data === null) {
    return <Loading label={tx('ui.locations.loading')} />;
  }
  if (resource.error && resource.data === null) {
    return <Failed message={resource.error} onRetry={resource.refresh} />;
  }

  return (
    <div className="xo-platform-page xo-locations-page">
      <Head title={tx('ui.locations.title')} subtitle={tx('ui.locations.subtitle')}>
        {mayManage && (
          <button type="button" className="xo-btn" onClick={startCreate}>
            <Plus aria-hidden="true" />{tx('ui.locations.create')}
          </button>
        )}
      </Head>

      <LocationNetwork locations={locations}
        responsibilities={resource.data?.responsibilities || []} />

      <div className="xo-location-workspace">
        <section className="xo-data-surface xo-location-list">
          {locations.length === 0 ? (
            <Empty title={tx('ui.locations.none')} detail={tx('ui.locations.create_first')} />
          ) : locations.map((location) => (
            <button type="button" key={location.id}
              className={selectedId === location.id && !creating ? 'is-selected' : ''}
              onClick={() => choose(location)}>
              <span className="xo-location-list-icon">
                {location.kind === 'office' ? <Building2 aria-hidden="true" />
                  : location.kind === 'warehouse' ? <Warehouse aria-hidden="true" />
                    : location.kind === 'retail' ? <ShoppingBag aria-hidden="true" /> : <FlaskConical aria-hidden="true" />}
              </span>
              <span>
                <strong>{location.name}</strong>
                <small>{location.code} · {codeLabel('locationKind', location.kind)} · {location.address.city}</small>
              </span>
              <span className="xo-location-list-marks">
                {location.isWebsiteDefault && <Pill tone="info">{tx('ui.store.default')}</Pill>}
                <Pill tone={location.isActive ? 'ok' : 'flat'}>
                  {location.isActive ? tx('ui.values.active') : tx('ui.values.inactive')}
                </Pill>
              </span>
            </button>
          ))}
        </section>

        <section className="xo-data-surface xo-location-editor">
          <header>
            <div>
              <p className="xo-eyebrow">{creating ? tx('ui.locations.new_location') : form.code}</p>
              <h2>{creating ? tx('ui.locations.create') : form.name}</h2>
            </div>
          </header>

          <form className="xo-form" onSubmit={save}>
            <div className="xo-grid">
              <Field id="location-code" label={tx('ui.locations.code')}>
                <input {...fieldAria('location-code')} type="text" value={form.code} required maxLength={16}
                  disabled={!mayManage || saving} onChange={(event) => set('code', event.target.value.toUpperCase())} />
              </Field>
              <Field id="location-kind" label={tx('ui.locations.kind')}>
                <XomicsSelect {...fieldAria('location-kind')} value={form.kind}
                  disabled={!mayManage || saving} onChange={(event) => set('kind', event.target.value)}>
                  <option value="office">{codeLabel('locationKind', 'office')}</option>
                  <option value="laboratory">{codeLabel('locationKind', 'laboratory')}</option>
                  <option value="warehouse">{codeLabel('locationKind', 'warehouse')}</option>
                  <option value="retail">{codeLabel('locationKind', 'retail')}</option>
                </XomicsSelect>
              </Field>
              <Field id="location-name" label={tx('ui.locations.name')}>
                <input {...fieldAria('location-name')} type="text" value={form.name} required
                  disabled={!mayManage || saving} onChange={(event) => set('name', event.target.value)} />
              </Field>
              <Field id="location-legal" label={tx('ui.locations.legal_name')}>
                <input {...fieldAria('location-legal')} type="text" value={form.legalName}
                  disabled={!mayManage || saving} onChange={(event) => set('legalName', event.target.value)} />
              </Field>
              <Field id="location-email" label={tx('ui.locations.email')}>
                <input {...fieldAria('location-email')} type="email" value={form.email}
                  disabled={!mayManage || saving} onChange={(event) => set('email', event.target.value)} />
              </Field>
              <Field id="location-phone" label={tx('ui.locations.phone')}>
                <input {...fieldAria('location-phone')} type="tel" value={form.phone}
                  disabled={!mayManage || saving} onChange={(event) => set('phone', event.target.value)} />
              </Field>
            </div>

            <Field id="location-address" label={tx('ui.locations.verified_address')}
              hint={tx('ui.locations.address_hint')}>
              <AddressPicker search={searchLocationPlaces} details={loadLocationPlace}
                value={form.addressLabel} disabled={!mayManage || saving}
                onSelect={(address) => setForm((current) => ({
                  ...current,
                  placeId: address.placeId,
                  addressChanged: true,
                  addressLabel: address.formattedAddress,
                  address: {
                    line1: address.line1 || '',
                    line2: address.line2 || '',
                    postalCode: address.postalCode || '',
                    city: address.city || '',
                    region: address.region || '',
                    countryCode: address.countryCode || '',
                  },
                  coordinates: {
                    latitude: address.latitude,
                    longitude: address.longitude,
                  },
                }))} />
            </Field>
            <StructuredAddress address={form.address} coordinates={form.coordinates}
              disabled={!mayManage || saving}
              onLine2Change={(line2) => setForm((current) => ({
                ...current, address: { ...current.address, line2 },
              }))} />

            {form.kind === 'office' && (
              <CountryResponsibilityEditor value={form.responsibleCountries}
                onChange={(countries) => set('responsibleCountries', countries)}
                responsibilities={resource.data?.responsibilities || []}
                locations={locations} currentLocationId={form.id}
                disabled={!mayManage || saving} />
            )}

            <label className="xo-check">
              <input type="checkbox" checked={form.isActive} disabled={!mayManage || saving}
                onChange={(event) => set('isActive', event.target.checked)} />
              {tx('ui.locations.active')}
            </label>
            {form.kind === 'office' && (
              <label className="xo-check">
                <input type="checkbox" checked={form.showOnWebsite} disabled={!mayManage || saving}
                  onChange={(event) => set('showOnWebsite', event.target.checked)} />
                {tx('ui.locations.show_on_website')}
              </label>
            )}
            {form.kind === 'office' && form.showOnWebsite && (
              <Field id="location-website-order" label={tx('ui.locations.website_order')}>
                <input {...fieldAria('location-website-order')} type="number" min={0} max={10000} step={1}
                  value={form.websiteOrder} disabled={!mayManage || saving}
                  onChange={(event) => set('websiteOrder', event.target.value)} />
              </Field>
            )}
            {/* One office holds the mark: it is given by ticking another office, never taken off. */}
            {form.kind === 'office' && form.isActive && (
              <label className="xo-check" title={tx('ui.store.default_hint')}>
                <input type="checkbox" checked={form.isWebsiteDefault}
                  disabled={!mayManage || saving || (!creating && selected?.isWebsiteDefault === true)}
                  onChange={(event) => set('isWebsiteDefault', event.target.checked)} />
                {tx('ui.store.default')}
              </label>
            )}

            {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
            {notice && <p className="xo-alert t-ok" role="status">{notice}</p>}
            {mayManage && (
              <div className="xo-bar-group">
                <button type="submit" className="xo-btn" disabled={saving || !form.code || !form.name}>
                  <Save aria-hidden="true" />{saving ? tx('ui.locations.saving') : tx('ui.locations.save')}
                </button>
                {creating && (
                  <button type="button" className="xo-btn-2" disabled={saving}
                    onClick={() => {
                      setCreating(false);
                      if (locations[0]) choose(locations[0]);
                    }}>
                    {tx('ui.common.cancel')}
                  </button>
                )}
              </div>
            )}
          </form>
        </section>
      </div>
    </div>
  );
}
